import { useContext, useEffect, useState } from 'react';
import { Card, Divider, Spin, Tag, Tooltip } from 'antd';
import {
    CalendarOutlined,
    CheckCircleOutlined,
    GiftOutlined,
    TeamOutlined,
    TrophyOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { IDashboardFinishedRaffle } from '../../../../services/http/mappers/DashboardMapper';
import { ThemeContext } from '../../../../contexts/ThemeContext';
import { convertDataUSParaBrasilEHora, convertMoedaBRDoJSNovo, poeValorComPontosEmMil } from '../../../../utils/Conversores';

dayjs.locale('pt-br');

interface FinishedRafflesListProps {
    data: IDashboardFinishedRaffle[];
    isLoading: boolean;
}

export default function FinishedRafflesList({ data, isLoading }: FinishedRafflesListProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    const totalRaffles = data?.length ?? 0;
    const totalDistributed = data?.reduce((acc, r) => acc + (r.totalPrizeValue || 0), 0) ?? 0;
    const totalTicketsAll = data?.reduce((acc, r) => acc + (r.totalTickets || 0), 0) ?? 0;

    function handleNavigate(raffle: IDashboardFinishedRaffle) {
        const path = raffle.kind === 'auto'
            ? `/sorteios/auto/visualizar/${raffle.id}`
            : `/sorteios/geral/visualizar/${raffle.id}`;
        navigate(path);
    }

    return (
        <Card
            title={
                <div style={{ paddingTop: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 20 }}>Sorteios Realizados e Premiações</div>
                    <div style={{ fontWeight: 400, fontSize: 18, color: '#888' }}>
                        Histórico de sorteios concluídos e valores distribuídos
                    </div>
                </div>
            }
            style={{
                background: isDark ? '#1f1f1f' : '#FFFFFF',
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                border: 'none',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
            bodyStyle={{ padding: '8px 20px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}
        >
            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Spin />
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                            gap: 12,
                        }}
                    >
                        <div
                            style={{
                                padding: '12px 16px',
                                borderRadius: 10,
                                background: isDark ? '#141414' : '#fafafa',
                                border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                                borderLeft: '4px solid #52c41a',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#52c41a', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <TrophyOutlined style={{ fontSize: 14 }} />
                                <span>Total em Prêmios</span>
                            </div>
                            <div style={{ fontSize: 22, fontWeight: 700, color: '#52c41a', marginTop: 4, lineHeight: 1.2 }}>
                                R$ {convertMoedaBRDoJSNovo(totalDistributed)}
                            </div>
                            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Distribuídos aos munícipes</div>
                        </div>

                        <div
                            style={{
                                padding: '12px 16px',
                                borderRadius: 10,
                                background: isDark ? '#141414' : '#fafafa',
                                border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                                borderLeft: '4px solid #722ed1',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#722ed1', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <GiftOutlined style={{ fontSize: 14 }} />
                                <span>Bilhetes Gerados</span>
                            </div>
                            <div style={{ fontSize: 22, fontWeight: 700, color: '#722ed1', marginTop: 4, lineHeight: 1.2 }}>
                                {poeValorComPontosEmMil(totalTicketsAll)}
                            </div>
                            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Concorreram nos sorteios</div>
                        </div>

                        <div
                            style={{
                                padding: '12px 16px',
                                borderRadius: 10,
                                background: isDark ? '#141414' : '#fafafa',
                                border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                                borderLeft: '4px solid #1677ff',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#1677ff', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <CheckCircleOutlined style={{ fontSize: 14 }} />
                                <span>Sorteios Realizados</span>
                            </div>
                            <div style={{ fontSize: 22, fontWeight: 700, color: '#1677ff', marginTop: 4, lineHeight: 1.2 }}>
                                {totalRaffles}
                            </div>
                            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Concluídos com sucesso</div>
                        </div>
                    </div>

                    <div style={{ flex: 1, minHeight: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 600, color: isDark ? '#aaa' : '#666', marginBottom: 10 }}>
                            Histórico de Sorteios
                        </div>

                        {data.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#888', padding: '30px 0', fontSize: 14 }}>
                                Nenhum sorteio finalizado encontrado
                            </div>
                        ) : (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10,
                                    maxHeight: 480,
                                    overflowY: 'auto',
                                    paddingRight: 4,
                                }}
                            >
                                {data.map((raffle) => {
                                    const date = dayjs(raffle.dateOfRaffle);
                                    const day = date.format('DD');
                                    const month = date.format('MMM').toUpperCase().replace('.', '');
                                    const isAuto = raffle.kind === 'auto';
                                    const accentColor = isAuto ? '#722ed1' : '#1677ff';

                                    return (
                                        <div
                                            key={raffle.id}
                                            onClick={() => handleNavigate(raffle)}
                                            style={{
                                                display: 'flex',
                                                flexDirection: isMobile ? 'column' : 'row',
                                                alignItems: isMobile ? 'stretch' : 'center',
                                                gap: 12,
                                                padding: '12px 16px',
                                                borderRadius: 10,
                                                background: isDark ? '#141414' : '#fafafa',
                                                border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                                                borderLeft: `4px solid ${accentColor}`,
                                                cursor: 'pointer',
                                                transition: 'all 0.18s ease',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = isDark ? '#1c1f26' : '#f4f8ff';
                                                e.currentTarget.style.borderColor = '#91caff';
                                                e.currentTarget.style.borderLeftColor = accentColor;
                                                e.currentTarget.style.transform = 'translateY(-1px)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(22,119,255,0.08)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = isDark ? '#141414' : '#fafafa';
                                                e.currentTarget.style.borderColor = isDark ? '#262626' : '#eaedf1';
                                                e.currentTarget.style.borderLeftColor = accentColor;
                                                e.currentTarget.style.transform = 'none';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                        >
                                            {!isMobile && (
                                                <div
                                                    style={{
                                                        minWidth: 56,
                                                        width: 56,
                                                        height: 56,
                                                        borderRadius: 8,
                                                        background: isDark ? '#1a2233' : '#e6f4ff',
                                                        border: `1px solid ${isDark ? '#17365d' : '#adc6ff'}`,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <span style={{ fontSize: 16, fontWeight: 800, color: '#1677ff', lineHeight: 1 }}>{day}</span>
                                                    <span style={{ fontSize: 10, fontWeight: 700, color: '#1677ff' }}>
                                                        {month}
                                                    </span>
                                                    <span style={{ fontSize: 10, fontWeight: 700, color: '#1677ff' }}>
                                                        {date.format('YYYY')}
                                                    </span>
                                                </div>
                                            )}

                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Tooltip title={raffle.description}>
                                                            <div
                                                                style={{
                                                                    fontSize: 16,
                                                                    fontWeight: 600,
                                                                    color: isDark ? '#e8e8e8' : '#222',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    flex: 1,
                                                                }}
                                                            >
                                                                {raffle.description}
                                                            </div>
                                                        </Tooltip>
                                                        <Tag
                                                            color={isAuto ? 'purple' : 'blue'}
                                                            style={{ borderRadius: 6, fontSize: 12, margin: 0, fontWeight: 500 }}
                                                        >
                                                            {isAuto ? 'IPTU' : 'Notas Fiscais'}
                                                        </Tag>
                                                    </div>

                                                </div>

                                                <div style={{ fontSize: 13, color: '#888', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <CalendarOutlined style={{ fontSize: 12 }} />
                                                    <span>Período: {convertDataUSParaBrasilEHora(raffle.startPeriod)} a {convertDataUSParaBrasilEHora(raffle.endPeriod)}</span>
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: 6,
                                                    alignItems: 'center',
                                                    justifyContent: isMobile ? 'space-between' : 'flex-end',
                                                    borderTop: isMobile ? `1px solid ${isDark ? '#2a2a2a' : '#f0f0f0'}` : 'none',
                                                    paddingTop: isMobile ? 8 : 0,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <div style={{ textAlign: 'right', minWidth: 80 }}>
                                                    <div style={{ fontSize: 11, color: '#888', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                                                        <TeamOutlined style={{ color: '#1677ff', fontSize: 11 }} />
                                                        <span>Participantes</span>
                                                    </div>
                                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#1677ff', marginTop: 1, lineHeight: 1.1 }}>
                                                        {poeValorComPontosEmMil(raffle.totalParticipants)}
                                                    </div>
                                                </div>

                                                <Divider
                                                    type="vertical"
                                                    style={{
                                                        height: 28,
                                                        margin: '0 6px',
                                                        borderColor: isDark ? '#333' : '#e0e0e0',
                                                    }}
                                                />

                                                <div style={{ textAlign: 'right', minWidth: 80 }}>
                                                    <div style={{ fontSize: 11, color: '#888', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                                                        <GiftOutlined style={{ color: '#722ed1', fontSize: 11 }} />
                                                        <span>Bilhetes</span>
                                                    </div>
                                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#722ed1', marginTop: 1, lineHeight: 1.1 }}>
                                                        {poeValorComPontosEmMil(raffle.totalTickets)}
                                                    </div>
                                                </div>

                                                <Divider
                                                    type="vertical"
                                                    style={{
                                                        height: 28,
                                                        margin: '0 6px',
                                                        borderColor: isDark ? '#333' : '#e0e0e0',
                                                    }}
                                                />

                                                {/* Premiação */}
                                                <div style={{ textAlign: 'right', minWidth: 100 }}>
                                                    <div style={{ fontSize: 11, color: '#888', fontWeight: 500 }}>
                                                        Prêmios Pagos
                                                    </div>
                                                    <div style={{ fontSize: 16, fontWeight: 800, color: '#389e0d', marginTop: 1, lineHeight: 1.1 }}>
                                                        R$ {convertMoedaBRDoJSNovo(raffle.totalPrizeValue)}
                                                    </div>
                                                </div>

                                                <FaChevronRight style={{ fontSize: 13, color: '#bfbfbf', marginLeft: 6 }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
}
